import type { User } from 'oidc-client-ts';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import i18n from '../i18n';
import { userManager } from './oidc';
import { renewalFailure, returnPath, type RenewalFailure, type SignInState } from './session';

/**
 * Keycloak's pages open in the language of the portal (OpenID Connect ui_locales), and the
 * request remembers the page to come back to.
 */
function signinArgs() {
  const { pathname, search, hash } = window.location;
  const state: SignInState = { returnTo: `${pathname}${search}${hash}` };
  return { ui_locales: i18n.language.slice(0, 2), state };
}

/**
 * Keycloak pages the portal opens for the signed-in user (application-initiated actions): an
 * authenticator application, a passkey, recovery codes, a new password. Keycloak comes back to the
 * page the user left.
 */
export type AccountAction =
  | 'CONFIGURE_TOTP'
  | 'webauthn-register-passwordless'
  | 'CONFIGURE_RECOVERY_AUTHN_CODES'
  | 'UPDATE_PASSWORD';

interface AuthState {
  user: User | null;
  loading: boolean;
  signIn: () => Promise<void>;
  /** A new sign-in even with an open Keycloak session (two-step verification just required). */
  reauthenticate: () => Promise<void>;
  startAccountAction: (action: AccountAction) => Promise<void>;
  signOut: () => Promise<void>;
  accessToken: () => string | undefined;
  /** After a 401: a valid token to retry with, or undefined (see authorizedFetch). */
  renewSession: (failedToken: string) => Promise<string | undefined>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

/**
 * The authorization code can be exchanged only once; React StrictMode runs effects twice in
 * development, so the callback is processed once per page load and its promise shared.
 */
let callbackOnce: Promise<User | undefined> | undefined;
function processSigninCallback(): Promise<User | undefined> {
  callbackOnce ??= userManager.signinCallback().then((u) => u ?? undefined);
  return callbackOnce;
}

/**
 * One renewal at a time with the refresh token, shared by the expiry event and every request
 * refused meanwhile: a dashboard sends a dozen requests at once.
 */
type Renewal = { user: User } | { failure: RenewalFailure };
let renewal: Promise<Renewal> | undefined;
function renewOnce(): Promise<Renewal> {
  renewal ??= userManager
    .signinSilent()
    .then((user): Renewal => (user ? { user } : { failure: 'ended' }))
    .catch((error: unknown): Renewal => {
      console.warn('The session could not be renewed', error);
      return { failure: renewalFailure(error) };
    })
    .finally(() => {
      renewal = undefined;
    });
  return renewal;
}

let signingInAgain = false;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  /** The session is over at Keycloak: back to its sign-in page, then to the same page here. */
  const signInAgain = useCallback(async () => {
    if (signingInAgain) return;
    signingInAgain = true;
    setLoading(true);
    try {
      await userManager.removeUser();
      await userManager.signinRedirect(signinArgs());
    } catch (error) {
      // Keycloak unreachable: the application's sign-in page, to try again by hand
      console.error('Could not open the sign-in page', error);
      signingInAgain = false;
      setLoading(false);
    }
  }, []);

  const renewSession = useCallback(
    async (failedToken: string) => {
      // another request may have renewed the session while this one was on its way
      const stored = await userManager.getUser();
      if (stored && !stored.expired && stored.access_token !== failedToken) {
        return stored.access_token;
      }
      const result = await renewOnce();
      if ('user' in result) return result.user.access_token;
      if (result.failure === 'ended') void signInAgain();
      return undefined;
    },
    [signInAgain],
  );

  useEffect(() => {
    let cancelled = false;
    let redirecting = false;
    (async () => {
      try {
        if (window.location.pathname === '/auth/callback') {
          const signedIn = await processSigninCallback();
          if (!cancelled) {
            setUser(signedIn ?? null);
            navigateRef.current(returnPath(signedIn?.state), { replace: true });
          }
        } else {
          let current = await userManager.getUser();
          // someone who just joined through an invitation lands here signed in at Keycloak
          // (FR-TRV-11): sign in to the application with that account, unless it is already
          // the one of this browser session
          const landing = new URLSearchParams(window.location.search);
          const session = landing.has('iss') ? landing.get('session_state') : null;
          if (session) {
            window.history.replaceState({}, '', window.location.pathname);
            if (!current || current.expired || current.session_state !== session) {
              redirecting = true;
              await userManager.removeUser();
              await userManager.signinRedirect(signinArgs());
              return;
            }
          }
          // a tab reopened after its access token expired: renew it, or sign in again when the
          // session itself is over (offline: the sign-in page, to try again by hand)
          if (current?.expired) {
            const result = await renewOnce();
            if ('failure' in result && result.failure === 'ended') {
              redirecting = true;
              await signInAgain();
              return;
            }
            current = 'user' in result ? result.user : null;
          }
          if (!cancelled) setUser(current);
        }
      } catch (error) {
        console.error('Authentication failed', error);
      } finally {
        if (!cancelled && !redirecting) setLoading(false);
      }
    })();
    const onLoaded = (u: User) => setUser(u);
    const onUnloaded = () => setUser(null);
    // automaticSilentRenew renews a minute before expiry; when that failed (Keycloak refused, or
    // the computer slept through it), the expiry decides: renew once more or sign in again
    const onExpired = () => {
      void userManager.getUser().then((u) => u?.access_token && renewSession(u.access_token));
    };
    userManager.events.addUserLoaded(onLoaded);
    userManager.events.addUserUnloaded(onUnloaded);
    userManager.events.addAccessTokenExpired(onExpired);
    return () => {
      cancelled = true;
      userManager.events.removeUserLoaded(onLoaded);
      userManager.events.removeUserUnloaded(onUnloaded);
      userManager.events.removeAccessTokenExpired(onExpired);
    };
  }, [renewSession, signInAgain]);

  const signIn = useCallback(() => userManager.signinRedirect(signinArgs()), []);
  const reauthenticate = useCallback(
    () => userManager.signinRedirect({ ...signinArgs(), prompt: 'login' }),
    [],
  );
  const startAccountAction = useCallback(
    (action: AccountAction) =>
      userManager.signinRedirect({ ...signinArgs(), extraQueryParams: { kc_action: action } }),
    [],
  );
  const signOut = useCallback(() => userManager.signoutRedirect(), []);
  const accessToken = useCallback(() => user?.access_token, [user]);

  const value = useMemo(
    () => ({
      user,
      loading,
      signIn,
      reauthenticate,
      startAccountAction,
      signOut,
      accessToken,
      renewSession,
    }),
    [user, loading, signIn, reauthenticate, startAccountAction, signOut, accessToken, renewSession],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
